import {
    Component,
    Host,
    Prop,
    h,
    Listen,
    State,
    Watch,
    Element,
    Event,
    EventEmitter,
} from "@stencil/core";
import { debounce } from "../../../utils/debounce";
import { isCookieConsentAccepted } from "../../../utils/cookieConsent";
import { setCookie } from "../../../utils/setCookie";
import { desktopBreakpoint } from "../shared-variables";
import api from "../../../utils/api";

interface LocaleType {
    localeLabel: string;
    localeValue: string;
    languageISOCode: string;
    localeURL: string;
    preselected?: boolean;
}

interface LocalesResponseData {
    availableLocales: LocaleType[];
}

@Component({
    tag: "ux-iws-navigation-locale-selection",
    styleUrl: "navigation-locale-selection.scss",
})
export class NavigationLocaleSelection {
    private baseClass = "ux-iws-navigation-locale-selection";
    private activeBodyClass = "ux-iws-nav-active--locale-selection";
    private selectElement: HTMLUxSelectElement;

    @Element() element: HTMLUxIwsNavigationLocaleSelectionElement;

    /** the text of the close button */
    @Prop() closeText: string = "Close";
    /** the text of the accept button */
    @Prop() acceptText: string = "Accept selection";
    /** If this component is used in the shop */
    @Prop() isShop: boolean = false;
    /** if set, the available languages will be fetched asynchronously */
    @Prop() asyncUrl: string | null = null;

    @State() localesData: LocalesResponseData = null;
    @State() isWaiting: boolean = true;
    @State() isOpen: boolean = false;
    @State() highestSubNavigation: number | null = null;
    @State() wrapperHeight: number | null = null;

    componentDidLoad() {
        this.setWrapperHeight();
    }

    /** event emitted when the locale selection is closed */
    @Event({
        eventName: "uxNavigationLocaleSelection",
        composed: true,
        cancelable: true,
        bubbles: true,
    })
    navigationLocaleSelectionOpen: EventEmitter<{ isOpen: boolean }>;

    @Listen("uxNavigationLocaleSelection", { target: "window" })
    toggleLocaleSelectionVisibility(e: CustomEvent) {
        this.isOpen = e.detail.isOpen;
        const pageContents = document.querySelectorAll(
            ".ux-iws-nav-blur-page, .ux-iws-header__navigation-wrapper, .ux-iws-footer"
        );
        // timeout makes sure that the inert attribute is removed via re-render before the focus is set
        window.setTimeout(() => {
            if (this.isOpen) {
                pageContents.forEach((el) => el.setAttribute("inert", "true"));
                const select = this.element.querySelector("select");
                if (select !== null) {
                    select.focus({ focusVisible: true } as FocusOptions);
                } else {
                    this.element
                        .querySelector("button")
                        ?.focus({ focusVisible: true } as FocusOptions);
                }
            } else {
                pageContents.forEach((el) => el.removeAttribute("inert"));
            }
        }, 100);
    }

    @Listen("uxIwsNavHighestSubNavigation", { target: "window" })
    setHighestSubNavigation(e: CustomEvent) {
        const highestSubNavigation = e.detail;
        const headerHeight = 100;
        this.highestSubNavigation =
            window.innerHeight <= highestSubNavigation + headerHeight
                ? window.innerHeight
                : highestSubNavigation + headerHeight;
    }

    @Listen("resize", { target: "window" })
    handleResize() {
        this.onResize();
    }

    private close = () => {
        this.navigationLocaleSelectionOpen.emit({ isOpen: false });
    };

    private onResize = debounce(() => this.setWrapperHeight(), 300);

    @Watch("isOpen")
    handleBodyClass() {
        const body = document.body as HTMLElement;
        if (this.isOpen) {
            if (window.innerWidth < desktopBreakpoint) {
                const header = document.querySelector("ux-iws-header");
                header?.scrollTo({
                    top: 0,
                    behavior: "auto",
                });
            }

            body.classList.add(this.activeBodyClass);
            this.setWrapperHeight();

            if (this.isWaiting) {
                if (this.asyncUrl) {
                    api<LocalesResponseData>(this.asyncUrl)
                        .then((response) => {
                            this.localesData = response;
                            this.isWaiting = false;
                        })
                        .catch(() => {
                            this.isWaiting = true;
                        });
                }
            }
        } else body.classList.remove(this.activeBodyClass);
    }

    private getSelect = (): HTMLSelectElement | null => {
        if (!this.selectElement) return null;
        return this.selectElement.querySelector("select");
    };

    private changeLocale = async () => {
        const selectElement = this.getSelect();
        if (selectElement === null) return;
        const selectedOption =
            selectElement.options[selectElement.selectedIndex];
        if (!selectedOption) return;
        const selectedLocaleValue = selectedOption.value;
        let selectedUrl = selectedOption.dataset.url;
        const selectedUid = selectedOption.dataset.languageIso;

        if (this.isShop) {
            selectedUrl =
                this.addCurrentPageParameterToChangeLocaleUrl(selectedUrl);
        }

        if (selectedLocaleValue && selectedUrl && selectedUid) {
            let cookieExpires = null;
            isCookieConsentAccepted()
                .then(() => {
                    /**
                     *   TODO: this implementation of the cookie is
                     *   not optimal, we do not set the language ISO code,
                     *   but an internal identification number.
                     *   Check with backend team if this can be changed.
                     */
                    cookieExpires = 180 * 24 * 60 * 60 * 1000;
                })
                .catch(() => {})
                .finally(() => {
                    setCookie({
                        expiresIn: cookieExpires,
                        mainBody: `language_iso=${selectedUid};`,
                    });
                    window.location.href = selectedUrl;
                });
        }
    };

    private addCurrentPageParameterToChangeLocaleUrl = (
        selectedUrl: string
    ) => {
        const currentUrl = window.location.host + window.location.pathname;
        const urlParams = window.location.search;

        if (selectedUrl.includes("?")) {
            selectedUrl += "&RedirectUrlPath=" + encodeURIComponent(currentUrl);
        } else {
            selectedUrl += "?RedirectUrlPath=" + encodeURIComponent(currentUrl);
        }
        selectedUrl += "&RedirectUrlParams=" + encodeURIComponent(urlParams);
        return selectedUrl;
    };

    private setWrapperHeight = () => {
        if (window.innerWidth >= desktopBreakpoint) {
            this.wrapperHeight = this.highestSubNavigation;
        } else {
            this.wrapperHeight = null;
        }
    };

    render() {
        return (
            <Host
                class={{
                    [this.baseClass]: true,
                    [`${this.baseClass}--open`]: this.isOpen,
                }}
                aria-hidden={this.isOpen ? null : "true"}
                inert={!this.isOpen}
                data-testid="locale-selection"
            >
                <div
                    class={`${this.baseClass}__wrapper`}
                    style={
                        this.wrapperHeight
                            ? {
                                  height: `${this.wrapperHeight}px`,
                              }
                            : undefined
                    }
                >
                    <div class={`${this.baseClass}__headline-container`}>
                        <slot name="headline" />
                    </div>
                    <div class={`${this.baseClass}__selection-container`}>
                        <div class={`${this.baseClass}__select-container`}>
                            {this.asyncUrl === null && (
                                <ux-select
                                    data-testid="locale-select"
                                    ref={(el: HTMLUxSelectElement) =>
                                        (this.selectElement = el)
                                    }
                                >
                                    <slot />
                                </ux-select>
                            )}
                            {this.asyncUrl && this.isWaiting && (
                                <div class="ux-horizontal-loader"></div>
                            )}
                            {this.asyncUrl && !this.isWaiting && (
                                <ux-select
                                    data-testid="locale-select"
                                    ref={(el: HTMLUxSelectElement) =>
                                        (this.selectElement = el)
                                    }
                                >
                                    {this.localesData.availableLocales?.map(
                                        (locale: LocaleType) => (
                                            <option
                                                value={locale.localeValue}
                                                key={locale.languageISOCode}
                                                selected={
                                                    locale.preselected
                                                        ? true
                                                        : null
                                                }
                                                data-url={locale.localeURL}
                                                data-language-iso={
                                                    locale.languageISOCode
                                                }
                                                data-testid={locale.localeValue}
                                            >
                                                {locale.localeLabel}
                                            </option>
                                        )
                                    )}
                                </ux-select>
                            )}
                        </div>
                        <div class={`${this.baseClass}__button-container`}>
                            {(!this.asyncUrl ||
                                (this.asyncUrl && !this.isWaiting)) && (
                                <ux-button
                                    variant="primary"
                                    onClick={this.changeLocale}
                                    data-testid="accept"
                                >
                                    {this.acceptText}
                                </ux-button>
                            )}
                        </div>
                    </div>
                    <button
                        onClick={this.close}
                        class={`${this.baseClass}__close-button`}
                        data-testid="close"
                    >
                        <span class={`${this.baseClass}__close-text`}>
                            {this.closeText}
                        </span>
                        <ux-icon name="close"></ux-icon>
                    </button>
                </div>
            </Host>
        );
    }
}
